import { z } from 'zod';
export const DEFERRED_DUE_DATE_LABEL = 'the invoice due date';
const UNPRINTABLE_CAPTURE_STATUSES = [
    'uncaptured',
    'not_enabled',
    'absorbed',
    'mixed_shape',
    'bounded',
    'disagreement',
    'out_of_range',
];
const UNPRINTABLE_FEE_STATUSES = [...UNPRINTABLE_CAPTURE_STATUSES, 'no_connection'];
export const INVOICE_CARD_FEE_PERCENT_RANGE = { min: 0.5, max: 25 };
export const INVOICE_ACH_FEE_AMOUNT_RANGE = { min: 0.1, max: 50 };
export const INVOICE_FEE_CAPTURE_STATUSES = ['printable', ...UNPRINTABLE_CAPTURE_STATUSES];
export const invoiceFeeCaptureStatusSchema = z.enum(INVOICE_FEE_CAPTURE_STATUSES);
export const invoiceMessageFeeSchema = z.union([
    z.object({ status: z.literal('printable'), value: z.number() }),
    z.object({ status: z.enum(UNPRINTABLE_FEE_STATUSES) }),
]);
export const UNREAD_INVOICE_MESSAGE_FEES = {
    ccFee: { status: 'uncaptured' },
    achFee: { status: 'uncaptured' },
};
export const INVOICE_MESSAGE_PLACEHOLDER_RE = /{{([a-z]+\.[a-z]+)}}/g;
export const KNOWN_INVOICE_MESSAGE_PLACEHOLDERS = [
    'due.date',
    'company.name',
    'payable.to',
    'remit.address',
    'cc.fee',
    'ach.fee',
];
export const QBO_CUSTOMER_MEMO_MAX = 1000;
export function invoiceMessageRoom(linkParagraph) {
    return linkParagraph === null
        ? QBO_CUSTOMER_MEMO_MAX
        : QBO_CUSTOMER_MEMO_MAX - linkParagraph.length - 2;
}
const PAY_PREFIX = 'Pay online:';
const PORTAL_PREFIX = 'Account portal:';
export function payOnlineParagraph(payUrl, portalUrl) {
    const pay = `${PAY_PREFIX} ${payUrl}`;
    return portalUrl === null ? pay : `${pay}\n${PORTAL_PREFIX} ${portalUrl}`;
}
const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const FEE_SENTENCE = 'Credit card payments incur a {{cc.fee}}% processing fee. ACH payments incur a ${{ach.fee}} flat fee.';
const ONE_TIME_HEAD = `ACCOUNT SUMMARY
Invoice is due {{due.date}}. Previous balance may be reflected in prior invoices.

ONLINE PAYMENT
Pay your full invoice total through the secure payment link on this invoice.`;
const RECURRING_HEAD = `ACCOUNT SUMMARY
Previous balance may be reflected in prior invoices.

ONLINE PAYMENT
Pay your full invoice total through the secure payment link on this invoice.`;
const MESSAGE_TAIL = `Partial pay or a failed transaction will result in a $20-$35 penalty fee.

CHECK PAYMENT
Please remit your check payment to {{remit.address}}, along with a copy of your invoice. Make checks payable to {{payable.to}} and include your invoice number and unit number.
Late fees may apply according to association policy.`;
export function buildDefaultInvoiceMessage(kind, fees) {
    const head = kind === 'one-time' ? ONE_TIME_HEAD : RECURRING_HEAD;
    const printable = fees.ccFee.status === 'printable' && fees.achFee.status === 'printable';
    const online = printable ? `${head}\n${FEE_SENTENCE}` : head;
    return `${online}\n${MESSAGE_TAIL}`;
}
export const INVOICE_MESSAGE_PLACEHOLDER_LEGEND = [
    { token: '{{due.date}}', description: 'Invoice due date', oneTimeOnly: true },
    { token: '{{company.name}}', description: 'Company name', oneTimeOnly: false },
    { token: '{{payable.to}}', description: 'Payable-to name', oneTimeOnly: false },
    { token: '{{remit.address}}', description: 'Remit address', oneTimeOnly: false },
    { token: '{{cc.fee}}', description: 'Card fee percent, from Payabli', oneTimeOnly: false },
    { token: '{{ach.fee}}', description: 'ACH fee amount, from Payabli', oneTimeOnly: false },
];
export const PAY_LINK_LEGEND = 'TEO adds the payment links under the message. Do not type them.';
export function formatInvoiceFee(value) {
    return String(Number(value.toFixed(2)));
}
function nonempty(value) {
    if (value === undefined)
        return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
}
export function formatLongDueDate(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const monthName = MONTH_NAMES[month - 1];
    if (!monthName)
        return null;
    const asUtc = new Date(Date.UTC(year, month - 1, day));
    if (asUtc.getUTCFullYear() !== year ||
        asUtc.getUTCMonth() !== month - 1 ||
        asUtc.getUTCDate() !== day) {
        return null;
    }
    return `${monthName} ${day}, ${year}`;
}
const fromText = (text) => text === null ? { ok: false, reason: 'unset' } : { ok: true, text };
function resolveFee(fee, range) {
    if (fee === undefined)
        return { ok: false, reason: 'fee_uncaptured' };
    if (fee.status !== 'printable')
        return { ok: false, reason: `fee_${fee.status}` };
    if (!Number.isFinite(fee.value) || fee.value < 0)
        return { ok: false, reason: 'fee_uncaptured' };
    if (fee.value === 0)
        return { ok: false, reason: 'fee_not_enabled' };
    if (fee.value < range.min || fee.value > range.max) {
        return { ok: false, reason: 'fee_out_of_range' };
    }
    return { ok: true, text: formatInvoiceFee(fee.value) };
}
function valueFor(name, values, kind, mode) {
    switch (name) {
        case 'due.date':
            if (kind === 'recurring')
                return { ok: false, reason: 'recurring_due_date' };
            if (mode === 'template')
                return { ok: true, text: DEFERRED_DUE_DATE_LABEL };
            return fromText(values.dueDate ? formatLongDueDate(values.dueDate) : null);
        case 'company.name':
            return fromText(nonempty(values.companyName));
        case 'payable.to':
            return fromText(nonempty(values.payableTo));
        case 'remit.address':
            return fromText(nonempty(values.remitAddress));
        case 'cc.fee':
            return resolveFee(values.ccFee, INVOICE_CARD_FEE_PERCENT_RANGE);
        case 'ach.fee':
            return resolveFee(values.achFee, INVOICE_ACH_FEE_AMOUNT_RANGE);
        default:
            return { ok: false, reason: 'unknown_token' };
    }
}
export function extractPayOnlineParagraph(text) {
    const pay = (text ?? '').split('\n\n').find((para) => para.trimStart().startsWith(PAY_PREFIX));
    if (!pay)
        return null;
    const trimmed = pay.trim();
    return trimmed === '' ? null : trimmed;
}
export function hasUnseparablePayLink(text) {
    const linkParagraphs = (text ?? '').split('\n\n').filter((para) => para.includes(PAY_PREFIX));
    if (linkParagraphs.length === 0)
        return false;
    if (linkParagraphs.length > 1)
        return true;
    const [pay, ...rest] = linkParagraphs[0].trim().split('\n');
    const portalOnly = rest.length === 0 || (rest.length === 1 && rest[0].startsWith(PORTAL_PREFIX));
    return !(pay.startsWith(PAY_PREFIX) && portalOnly);
}
export function stripPayOnlineParagraphs(text) {
    return text
        .split('\n\n')
        .filter((para) => !para.trimStart().startsWith(PAY_PREFIX))
        .join('\n\n')
        .trim();
}
export function renderInvoiceMessage(template, values, kind, mode = 'instance') {
    const stripped = stripPayOnlineParagraphs(template);
    const refusals = [];
    const seen = new Set();
    for (const match of stripped.matchAll(/{{([a-z]+\.[a-z]+)}}/g)) {
        const name = match[1];
        if (seen.has(name))
            continue;
        seen.add(name);
        const resolution = valueFor(name, values, kind, mode);
        if (!resolution.ok)
            refusals.push({ token: name, reason: resolution.reason });
    }
    if (refusals.length > 0)
        return { ok: false, refusals };
    const text = stripped.replace(/{{([a-z]+\.[a-z]+)}}/g, (token, name) => {
        const resolution = valueFor(name, values, kind, mode);
        return resolution.ok ? resolution.text : token;
    });
    return { ok: true, text };
}
export function unstatedInvoiceFees(message, fees) {
    const body = stripPayOnlineParagraphs(message);
    if (body.trim() === '')
        return [];
    const unstated = [];
    const check = (token, fee) => {
        if (fee === undefined || fee.status !== 'printable')
            return;
        if (body.includes(`{{${token}}}`))
            return;
        unstated.push({ token, value: fee.value });
    };
    check('cc.fee', fees.ccFee);
    check('ach.fee', fees.achFee);
    return unstated;
}
export function unstatedInvoiceFeeMessage(unstated) {
    const charges = unstated
        .map((fee) => fee.token === 'cc.fee'
        ? `a ${formatInvoiceFee(fee.value)}% card fee`
        : `a $${formatInvoiceFee(fee.value)} ACH fee`)
        .join(' and ');
    const tokens = unstated.map((fee) => `{{${fee.token}}}`).join(' and ');
    return `This paypoint charges ${charges}. This message does not use ${tokens}, so it states the fee only if you wrote it in yourself.`;
}
export function checkSubmittedInvoiceMessage(message) {
    const text = stripPayOnlineParagraphs(message);
    const leftover = [
        ...new Set([...text.matchAll(/{{([a-z]+\.[a-z]+)}}/g)].map((match) => match[1])),
    ];
    return leftover.length > 0 ? { ok: false, leftover } : { ok: true, text };
}
export function leftoverInSubmittedMessage(leftover) {
    const names = leftover.map((name) => `{{${name}}}`).join(', ');
    return `The message still has ${names} in it. Remove it before sending.`;
}
const REFUSAL_ADVICE = {
    unset: 'Fill it in on this screen, or remove it.',
    unknown_token: 'TEO does not know that placeholder. Remove it.',
    recurring_due_date: 'QuickBooks dates a recurring invoice "Due on receipt", so a due date cannot go in a recurring message. Remove it.',
    fee_uncaptured: 'TEO has not read this fee from Payabli yet. Run Refresh Paypoint on the payment connection, or remove it.',
    fee_no_connection: 'This association has no payment connection, so TEO has no fee to print. Remove it.',
    fee_not_enabled: 'Payabli does not charge this fee on this paypoint. Remove it.',
    fee_absorbed: 'The association absorbs this fee, so the payer is never charged it. Remove it.',
    fee_mixed_shape: 'Payabli charges this payment type a percentage and a flat amount, which this sentence cannot say. Remove it.',
    fee_bounded: 'Payabli caps this fee per payment, so one number cannot be right for every invoice. Remove it.',
    fee_disagreement: 'Payabli reports more than one fee for this payment type. Correct it in Payabli and run Refresh Paypoint, or remove it.',
    fee_out_of_range: 'The fee Payabli reports is outside the range TEO can state, so TEO will not print it. Check the paypoint in Payabli and run Refresh Paypoint, or remove it.',
};
export function leftoverPlaceholderMessage(refusals) {
    const byReason = new Map();
    for (const refusal of refusals) {
        const token = `{{${refusal.token}}}`;
        const tokens = byReason.get(refusal.reason) ?? [];
        if (!tokens.includes(token))
            tokens.push(token);
        byReason.set(refusal.reason, tokens);
    }
    return [...byReason.entries()]
        .map(([reason, tokens]) => `The message still has ${tokens.join(', ')} in it. ${REFUSAL_ADVICE[reason]}`)
        .join(' ');
}
export function seedRemitAddress(mailingAddress) {
    if (!mailingAddress)
        return '';
    const street = mailingAddress.street?.trim() ?? '';
    const city = mailingAddress.city?.trim() ?? '';
    const state = mailingAddress.state?.trim() ?? '';
    const zip = mailingAddress.zipCode?.trim() ?? '';
    const locality = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return [street, locality].filter(Boolean).join(', ');
}
export function seedCompanyName(ownership) {
    return ownership.legalEntityName?.trim() || ownership.businessName?.trim() || '';
}
//# sourceMappingURL=invoiceMessage.js.map